import React, { FC, useState } from "react"
import { useTranslation } from "react-i18next"

import { Flex } from "components/Flex"
import Button from "components/Button/Button"
import Text from "components/Text/Text"
import { IGetFreightUnit } from "models/Asset"
import FreightUnitsItem from "./FreightUnitsItem"
import FreightUnitModal from "./FreightUnitModal"

interface IFreightUnitsProps {
    freightUnits: IGetFreightUnit[]
    onChange?: (freightUnits: IGetFreightUnit[]) => void
}

const FreightUnits: FC<React.PropsWithChildren<IFreightUnitsProps>> = ({
    freightUnits,
    onChange,
}) => {
    const { t } = useTranslation()
    const [showFreightUnitAddModal, setShowFreightUnitAddModal] = useState(false)

    const handleChange = onChange
        ? (freightUnit: IGetFreightUnit, index: number) => {
            const freightUnitsCopy = freightUnits.slice()
            freightUnitsCopy[index] = freightUnit
            onChange(freightUnitsCopy)
        }
        : undefined

    const handleDelete = onChange
        ? (index: number) => {
            const freightUnitsCopy = freightUnits.slice()
            freightUnitsCopy.splice(index, 1)
            onChange(freightUnitsCopy)
        }
        : undefined

    const handleAdd = onChange
        ? (freightUnit: IGetFreightUnit) => {
            onChange([
                ...freightUnits,
                freightUnit,
            ])
        }
        : undefined

    return (
        <>
            <Flex justifyContent="space-between" wrap="wrap" gap="2.4rem" alignItems="center" className="mb-3">
                <Text type="h4">{t('freightUnit.freightUnits')}</Text>
                {!!onChange && freightUnits.length > 0 &&
                    <Flex alignItems="center" wrap="wrap" gap="2.4rem">
                        <Button
                            label={t("freightUnit.addFreightUnit")}
                            theme="secondary"
                            onClick={() => setShowFreightUnitAddModal(true)}
                        />
                    </Flex>
                }
            </Flex>
            {freightUnits.length > 0
                ? freightUnits.map((freightUnit, index) => (
                    <div key={index} className="py-2 border-grey-100-between">
                        <FreightUnitsItem
                            freightUnit={freightUnit}
                            onChange={handleChange ? newFreightUnit => handleChange(newFreightUnit, index) : undefined}
                            onDelete={handleDelete ? () => handleDelete(index) : undefined}
                        />
                    </div>
                ))
                : <div className="py-4 text-center">
                    {t("freightUnit.noFreightUnitsAdded")}
                    {!!onChange && <div>{t("freightUnit.wantToAddFreightUnits")}</div>}
                    <Button
                        label={t("freightUnit.addFreightUnit")}
                        onClick={() => setShowFreightUnitAddModal(true)}
                        theme="secondary"
                        className="mt-4"
                    />
                </div>
            }
            {showFreightUnitAddModal && !!handleAdd &&
                <FreightUnitModal
                    onSave={handleAdd}
                    onClose={() => setShowFreightUnitAddModal(false)}
                />
            }
        </>
    )
}

export default FreightUnits
